/**
 * PostHog analytics configuration for web app.
 * Initialize this BEFORE rendering the app.
 *
 * Required env var: VITE_POSTHOG_KEY
 * Optional env var: VITE_POSTHOG_HOST (defaults to US cloud)
 *
 * Usage:
 *   import { initPostHog } from './lib/posthog';
 *   initPostHog();
 */
import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

export function initPostHog(): void {
  if (typeof window === 'undefined' || initialized) return;

  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.info('[PostHog] Skipped - no VITE_POSTHOG_KEY configured');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Only create person profiles for identified users (saves costs)
    person_profiles: 'identified_only',
    // We'll track page views manually for SPA navigation
    capture_pageview: false,
    capture_pageleave: true,
    // Enable autocapture for click tracking, etc.
    autocapture: true,
    // Session recording config
    session_recording: {
      maskAllInputs: false, // We'll mask sensitive fields explicitly
      maskTextSelector: '[data-mask]', // Mask elements with data-mask attribute
    },
    // Load feature flags on init
    advanced_disable_feature_flags_on_first_load: false,
  });

  initialized = true;

  if (import.meta.env.DEV) {
    console.info('[PostHog] Initialized');
  }
}

/**
 * Check if PostHog is initialized and available.
 */
export function isPostHogAvailable(): boolean {
  return initialized && !!POSTHOG_KEY;
}

/**
 * Get the PostHog instance for direct access.
 * Returns the instance or a no-op object if not initialized.
 */
export { posthog };
